import { useEffect, useMemo, useRef, useState } from 'react';
import { AddOutlined, EditOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type {
  Checklist,
  ChecklistTemplate,
  ChecklistTemplateWorkerType,
  LifecycleView,
  LifecycleWorker,
} from '../model';
import type { LifecyclePort, LifecycleRoutePort } from '../port';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { formatTimestampDate } from '../../../shared/formatTimestampDate';
import {
  FeedbackSnackbar,
  DefinitionList,
  DetailDrawerBody,
  DetailPanel,
  DialogSurface,
  FormAutocomplete,
  FormDialog,
  FormTextField,
  ListDetailWorkspace,
  ResourcePage,
  PillTabs,
  ResourceCollection,
  StatusBadge,
  ValueBadge,
  type PillTabItem,
  type ResourceCollectionState,
} from '../../../design-system';
import {
  ChecklistTemplateEditor,
  checklistTemplateDraft,
  checklistTemplateInput,
  type ChecklistTemplateDraft,
} from '../ChecklistTemplateEditor';

const fixtureChecklist: Checklist = {
  id: 'checklist-1', workerId: '03', templateCode: 'ONBOARDING_DEFAULT', descriptor: '기본 온보딩', lifecycleType: 'ONBOARDING', status: 'IN_PROGRESS', targetOn: '2026-09-01', createdAt: '2026-08-15T00:00:00Z',
  items: [
    { id: 'item-1', descriptor: 'Google Workspace 계정 준비', executionType: 'INTEGRATION', assigneeType: 'ROLE', required: true, status: 'COMPLETED', completedAt: '2026-08-15T01:00:00Z' },
    { id: 'item-2', descriptor: '필수 입사 서류 확인', executionType: 'MANUAL', assigneeType: 'ROLE', required: true, status: 'PENDING', dueAt: '2026-09-01T00:00:00Z', note: '입사 서류를 확인한 뒤 완료해 주세요.' },
  ],
};

const fixtureWorkers: LifecycleWorker[] = [
  { id: '01', workerNumber: 'A0001', name: '정성진', workerType: 'Employee', workerTypeId: 'EMPLOYEE' },
  { id: '02', workerNumber: 'A0104', name: '우디(박진우)', workerType: 'Employee', workerTypeId: 'EMPLOYEE' },
  { id: '03', workerNumber: 'A0142', name: '김민서', workerType: 'Employee', workerTypeId: 'EMPLOYEE' },
  { id: '04', workerNumber: 'A0188', name: '이수연', workerType: 'Employee', workerTypeId: 'EMPLOYEE' },
  { id: '05', workerNumber: 'A0203', name: '한도윤', workerType: 'Employee', workerTypeId: 'EMPLOYEE' },
  { id: '06', workerNumber: 'A0221', name: '강예린', workerType: 'Employee', workerTypeId: 'EMPLOYEE' },
  { id: '07', workerNumber: 'A0238', name: '장우진', workerType: 'Employee', workerTypeId: 'EMPLOYEE' },
  { id: 'contingent-01', workerNumber: 'C0001', name: '외부 협력자', workerType: 'Contingent Worker', workerTypeId: 'CONTINGENT_WORKER' },
  { id: 'partial-01', workerNumber: 'P0001', name: '유형 미확인 구성원', workerType: '유형 미등록' },
];

const fixtureTemplates: ChecklistTemplate[] = [
  {
    id: 'ONBOARDING_DEFAULT', descriptor: '기본 온보딩', lifecycleType: 'ONBOARDING', workerScope: 'WORKER_TYPE', workerType: 'EMPLOYEE', version: 1, active: true, createdAt: '2026-08-15T00:00:00Z',
    items: [
      { id: 'GWS_ACCOUNT', descriptor: 'Google Workspace 계정 준비', executionType: 'INTEGRATION', integrationTarget: 'GOOGLE_WORKSPACE', dueOffsetDays: 0, required: true },
      { id: 'WELCOME_NOTICE', descriptor: 'Slack 입사 안내', executionType: 'INTEGRATION', integrationTarget: 'SLACK', dueOffsetDays: 0, required: true },
      { id: 'DOCUMENTS', descriptor: '필수 입사 서류 확인', executionType: 'MANUAL', dueOffsetDays: 0, required: true },
    ],
  },
  {
    id: 'OFFBOARDING_DEFAULT', descriptor: '기본 오프보딩', lifecycleType: 'OFFBOARDING', workerScope: 'WORKER_TYPE', workerType: 'EMPLOYEE', version: 1, active: true, createdAt: '2026-08-15T00:00:00Z',
    items: [
      { id: 'GWS_SUSPEND', descriptor: 'Google Workspace 계정 중지', executionType: 'INTEGRATION', integrationTarget: 'GOOGLE_WORKSPACE', dueOffsetDays: 0, required: true },
      { id: 'EXIT_NOTICE', descriptor: 'Slack 퇴사 안내', executionType: 'INTEGRATION', integrationTarget: 'SLACK', dueOffsetDays: 0, required: true },
      { id: 'ASSET_RETURN', descriptor: '자산 반납 확인', executionType: 'MANUAL', dueOffsetDays: 0, required: true },
    ],
  },
  {
    id: 'ALL_WORKERS', descriptor: '전 구성원 공통 안내', lifecycleType: 'ONBOARDING', workerScope: 'ALL', version: 1, active: true, createdAt: '2026-08-14T00:00:00Z',
    items: [
      { id: 'POLICY_GUIDE', descriptor: '공통 정책 안내', executionType: 'MANUAL', dueOffsetDays: 0, required: true },
    ],
  },
];

function createFixtureLifecyclePort(): LifecyclePort {
    let checklists = [structuredClone(fixtureChecklist)];
    let templates = structuredClone(fixtureTemplates);
    return {
      list: async () => structuredClone(checklists),
      listWorkers: async () => fixtureWorkers,
      listTemplates: async () => structuredClone(templates),
      create: async (workerId, templateId, targetOn) => {
        const template = templates.find((item) => item.id === templateId && item.active);
        if (!template) throw new Error('사용할 수 있는 템플릿을 찾지 못했습니다.');
        const created: Checklist = {
          id: `fixture-checklist-${checklists.length + 1}`,
          workerId,
          templateCode: template.id,
          descriptor: template.descriptor,
          lifecycleType: template.lifecycleType,
          status: 'IN_PROGRESS',
          targetOn,
          createdAt: new Date().toISOString(),
          items: template.items.map((item, index) => ({
            id: `fixture-checklist-item-${checklists.length + 1}-${index + 1}`,
            descriptor: item.descriptor,
            executionType: item.executionType,
            assigneeType: 'ROLE',
            required: item.required,
            status: 'PENDING',
          })),
        };
        checklists = [created, ...checklists];
        return structuredClone(created);
      },
      complete: async (checklistId, itemId) => {
        const checklist = checklists.find((item) => item.id === checklistId);
        if (!checklist) throw new Error('체크리스트를 찾지 못했습니다.');
        const updated: Checklist = {
          ...checklist,
          items: checklist.items.map((item) => item.id === itemId ? { ...item, status: 'COMPLETED', completedAt: new Date().toISOString() } : item),
        };
        checklists = checklists.map((item) => item.id === checklistId ? updated : item);
        return structuredClone(updated);
      },
      createTemplate: async (input, _idempotencyKey) => {
        const created: ChecklistTemplate = {
          ...structuredClone(input),
          id: `FIXTURE_${crypto.randomUUID().replaceAll('-', '').toUpperCase()}`,
          version: 1,
          active: true,
          createdAt: new Date().toISOString(),
          items: input.items.map((item, index) => ({ ...item, id: item.id || `ITEM_${index + 1}` })),
        };
        templates = [created, ...templates];
        return structuredClone(created);
      },
      createTemplateVersion: async (templateId, expectedVersion, input, _idempotencyKey) => {
        const current = templates.find((item) => item.id === templateId);
        if (!current) throw new Error('템플릿을 찾지 못했습니다.');
        if (current.version !== expectedVersion) throw new Error('다른 관리자가 템플릿을 변경했습니다. 목록을 새로고침해 주세요.');
        const updated: ChecklistTemplate = {
          ...structuredClone(input),
          id: templateId,
          version: current.version + 1,
          active: true,
          createdAt: new Date().toISOString(),
          items: input.items.map((item, index) => ({ ...item, id: item.id || `ITEM_${current.version + 1}_${index + 1}` })),
        };
        templates = templates.map((item) => item.id === templateId ? updated : item);
        return structuredClone(updated);
      },
      setTemplateActive: async (templateId, active, expectedVersion) => {
        const current = templates.find((item) => item.id === templateId);
        if (!current) throw new Error('템플릿을 찾지 못했습니다.');
        if (current.version !== expectedVersion) throw new Error('다른 관리자가 템플릿을 변경했습니다. 목록을 새로고침해 주세요.');
        const updated = { ...current, active };
        templates = templates.map((item) => item.id === templateId ? updated : item);
        return structuredClone(updated);
      },
    };
}

function lifecycleLabel(value: string) {
  return value === 'ONBOARDING' ? '온보딩' : value === 'OFFBOARDING' ? '오프보딩' : value;
}

function workerScopeLabel(template: ChecklistTemplate) {
  if (template.workerScope === 'ALL') return '전체 구성원';
  return template.workerType === 'EMPLOYEE' ? '임직원'
    : '외부 인력';
}

function checklistWorkerType(worker?: LifecycleWorker): ChecklistTemplateWorkerType | undefined {
  const id = worker?.workerTypeId?.trim().toUpperCase();
  if (id === 'EMPLOYEE' || id === 'CONTINGENT_WORKER') return id;
  const label = worker?.workerType.trim().toUpperCase();
  if (label === 'EMPLOYEE' || label === '임직원') return 'EMPLOYEE';
  if (label === 'CONTINGENT_WORKER' || label === 'CONTINGENT WORKER' || label === '외부 인력') return 'CONTINGENT_WORKER';
  return undefined;
}

function checklistStatus(value: string) {
  if (value === 'IN_PROGRESS') return { label: '진행 중', tone: 'warning' as const };
  if (value === 'COMPLETED') return { label: '완료', tone: 'success' as const };
  if (value === 'COMPLETED_WITH_EXCEPTIONS') return { label: '예외 포함 완료', tone: 'warning' as const };
  if (value === 'CANCELLED') return { label: '취소됨', tone: 'neutral' as const };
  return { label: value, tone: 'neutral' as const };
}

function itemStatusLabel(value: string) {
  if (value === 'COMPLETED') return '완료';
  if (value === 'FAILED') return '실패';
  if (value === 'IN_PROGRESS') return '진행 중';
  return value === 'PENDING' ? '대기' : value;
}

function executionLabel(value: string) {
  return value === 'INTEGRATION' ? '연동 작업' : value === 'MANUAL' ? '담당자 처리' : value;
}

function integrationLabel(value?: string) {
  if (value === 'GOOGLE_WORKSPACE') return 'Google Workspace 연동';
  if (value === 'SLACK') return 'Slack 연동';
  return '기존 연동 작업';
}

function templateMutationMessage(failure: unknown, fallback: string) {
  const code = failure && typeof failure === 'object' && 'code' in failure ? failure.code : undefined;
  if (code === 'CHECKLIST_TEMPLATE_VERSION_CONFLICT') {
    return '다른 관리자가 이 템플릿을 변경했습니다. 목록으로 돌아가 최신 버전을 다시 열어 주세요.';
  }
  if (code === 'IDEMPOTENCY_CONFLICT') {
    return '저장 내용이 바뀌었습니다. 입력값을 다시 확인한 뒤 저장해 주세요.';
  }
  return failure instanceof Error ? failure.message : fallback;
}

type ChecklistRow = { id: string; worker: string; type: string; targetOn: string; progress: string; status: string; source: Checklist };

const lifecycleTabs: readonly PillTabItem<LifecycleView>[] = [
  { value: 'checklists', label: '체크리스트', tabId: 'lifecycle-checklists-tab', panelId: 'lifecycle-checklists-panel' },
  { value: 'templates', label: '템플릿 관리', tabId: 'lifecycle-templates-tab', panelId: 'lifecycle-templates-panel' },
];

export type LifecyclePageProps = {
  port: LifecyclePort;
  route: LifecycleRoutePort;
  fixtureMode: boolean;
  canMutate: boolean;
};

export function LifecyclePage({
  port: httpPort,
  route,
  fixtureMode,
  canMutate,
}: LifecyclePageProps) {
  const port = useMemo(() => fixtureMode ? createFixtureLifecyclePort() : httpPort, [httpPort, fixtureMode]);
  const resource = useAsyncResource(() => port.list(), [port]);
  const workersResource = useAsyncResource(() => port.listWorkers(), [port]);
  const templatesResource = useAsyncResource(() => port.listTemplates(), [port]);
  const [activeView, setActiveView] = useState<LifecycleView>(() => route.read().view);
  const [selected, setSelected] = useState<Checklist>();
  const [detailOpen, setDetailOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [onboardingOnly, setOnboardingOnly] = useState(false);
  const [workerID, setWorkerID] = useState('');
  const [templateID, setTemplateID] = useState('');
  const [workerQuery, setWorkerQuery] = useState('');
  const [templatePickerQuery, setTemplatePickerQuery] = useState('');
  const [targetOn, setTargetOn] = useState('');
  const [query, setQuery] = useState('');
  const [templateQuery, setTemplateQuery] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [completingID, setCompletingID] = useState('');
  const [completionError, setCompletionError] = useState<{ checklistID: string; message: string }>();
  const [selectedTemplate, setSelectedTemplate] = useState<ChecklistTemplate>();
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ChecklistTemplate>();
  const [templateDraft, setTemplateDraft] = useState<ChecklistTemplateDraft>(() => checklistTemplateDraft());
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateIdempotencyKey, setTemplateIdempotencyKey] = useState(() => crypto.randomUUID());
  const [templateError, setTemplateError] = useState('');
  const [templateNotice, setTemplateNotice] = useState('');
  const [activationOpen, setActivationOpen] = useState(false);
  const [activationSaving, setActivationSaving] = useState(false);
  const [activationError, setActivationError] = useState('');
  const templateMutationInFlight = useRef(false);
  const activationButton = useRef<HTMLButtonElement>(null);
  const wasConfirmingActivation = useRef(false);
  useEffect(() => {
    const returnToPreview = wasConfirmingActivation.current && !activationOpen && Boolean(selectedTemplate);
    wasConfirmingActivation.current = activationOpen;
    if (!returnToPreview) return;
    const frame = window.requestAnimationFrame(() => activationButton.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [activationOpen, selectedTemplate?.id]);

  useEffect(() => {
    const sync = () => {
      const current = route.read();
      setActiveView(current.view);
      if (current.onboarding && canMutate) {
        setWorkerID(current.onboarding.workerId);
        setTargetOn(current.onboarding.targetOn ?? '');
        setTemplateID('');
        setWorkerQuery('');
        setTemplatePickerQuery('');
        setError(undefined);
        setOnboardingOnly(true);
        setDialogOpen(true);
      } else setDialogOpen(false);
    };
    sync();
    return route.subscribe(sync);
  }, [route, canMutate]);

  const changeView = (view: LifecycleView) => {
    setActiveView(view);
    setError(undefined);
    setTemplateNotice('');
    setSelected(undefined);
    setSelectedTemplate(undefined);
    setActivationOpen(false);
    setActivationError('');
    route.replace(view);
  };
  const rows: ChecklistRow[] = (resource.data ?? []).map((item) => ({
    id: item.id,
    worker: workersResource.data?.find((worker) => worker.id === item.workerId)?.name || '이름 미등록',
    type: lifecycleLabel(item.lifecycleType),
    targetOn: item.targetOn,
    progress: `${item.items.filter((child) => child.status === 'COMPLETED').length}/${item.items.length}`,
    status: checklistStatus(item.status).label,
    source: item,
  }));
  const visibleRows = rows.filter((row) => `${row.worker} ${row.type} ${row.targetOn} ${row.status}`.toLocaleLowerCase('ko').includes(query.trim().toLocaleLowerCase('ko')));
  const templates = templatesResource.data ?? [];
  const activeTemplates = templates.filter((template) => template.active && (!onboardingOnly || template.lifecycleType === 'ONBOARDING'));
  const selectedWorker = workersResource.data?.find((worker) => worker.id === workerID);
  const selectedWorkerType = checklistWorkerType(selectedWorker);
  const eligibleActiveTemplates = workerID
    ? activeTemplates.filter((template) => template.workerScope === 'ALL' ||
      (selectedWorkerType !== undefined && template.workerScope === 'WORKER_TYPE' && template.workerType === selectedWorkerType))
    : [];
  const visibleTemplates = templates.filter((template) => template.descriptor.toLocaleLowerCase('ko').includes(templateQuery.trim().toLocaleLowerCase('ko')));

  const create = () => {
    if (!canMutate) return;
    if (!workerID || !templateID || !targetOn) { setError('대상 구성원, 템플릿과 기준일을 선택해 주세요.'); return; }
    const template = eligibleActiveTemplates.find((item) => item.id === templateID);
    if (!selectedWorker || !template) { setError('대상 구성원과 사용 가능한 템플릿을 목록에서 다시 선택해 주세요.'); return; }
    if (template?.workerScope === 'WORKER_TYPE' && !selectedWorkerType) {
      setError('유형별 템플릿을 사용하려면 대상 구성원의 유형을 확인할 수 있어야 합니다.');
      return;
    }
    setSubmitLoading(true); setError(undefined);
    void port.create(workerID, templateID, targetOn).then((created) => {
      setDialogOpen(false); setSelected(created); setDetailOpen(true); route.replace('checklists'); resource.reload();
    }, (failure: unknown) => setError(failure instanceof Error ? failure.message : '체크리스트를 만들지 못했습니다.')).finally(() => setSubmitLoading(false));
  };
  const complete = (itemID: string) => {
    if (!canMutate || !selected || completingID) return;
    const checklistID = selected.id;
    setCompletingID(itemID); setCompletionError(undefined);
    void port.complete(checklistID, itemID).then((updated) => {
      setSelected((current) => current?.id === checklistID ? updated : current); resource.reload();
    }, (failure: unknown) => setCompletionError({ checklistID, message: failure instanceof Error ? failure.message : '항목을 완료하지 못했습니다.' })).finally(() => setCompletingID(''));
  };
  const openTemplateEditor = (template?: ChecklistTemplate) => {
    if (!canMutate || templateMutationInFlight.current || (template && !template.active)) return;
    setEditingTemplate(template);
    setTemplateDraft(checklistTemplateDraft(template));
    setTemplateIdempotencyKey(crypto.randomUUID());
    setTemplateError('');
    setTemplateEditorOpen(true);
    setSelectedTemplate(undefined);
    setActivationOpen(false);
    setActivationError('');
  };
  const saveTemplate = async () => {
    if (!canMutate || templateMutationInFlight.current) return;
    templateMutationInFlight.current = true;
    const wasEditing = Boolean(editingTemplate);
    setTemplateSaving(true); setTemplateError('');
    try {
      const input = checklistTemplateInput(templateDraft);
      let saved: ChecklistTemplate;
      if (editingTemplate) {
        saved = await port.createTemplateVersion(editingTemplate.id, editingTemplate.version, input, templateIdempotencyKey);
      } else {
        saved = await port.createTemplate(input, templateIdempotencyKey);
      }
      setTemplateEditorOpen(false);
      setEditingTemplate(undefined);
      setSelectedTemplate(saved);
      setTemplateNotice(wasEditing ? `“${saved.descriptor}” 새 버전을 활성화했습니다.` : `“${saved.descriptor}” 템플릿을 만들었습니다.`);
      templatesResource.reload();
    } catch (failure) {
      setTemplateError(templateMutationMessage(failure, '템플릿을 저장하지 못했습니다.'));
    } finally { templateMutationInFlight.current = false; setTemplateSaving(false); }
  };
  const changeActivation = async () => {
    if (!canMutate || !selectedTemplate || templateMutationInFlight.current) return;
    templateMutationInFlight.current = true;
    const target = selectedTemplate;
    const active = !target.active;
    setActivationSaving(true); setActivationError('');
    try {
      const updated = await port.setTemplateActive(target.id, active, target.version);
      setSelectedTemplate(updated);
      setActivationOpen(false);
      setTemplateNotice(active ? `“${updated.descriptor}” 템플릿을 다시 사용합니다.` : `“${updated.descriptor}” 템플릿 사용을 종료했습니다.`);
      templatesResource.reload();
    } catch (failure) {
      setActivationError(templateMutationMessage(failure, '템플릿 상태를 변경하지 못했습니다.'));
    } finally { templateMutationInFlight.current = false; setActivationSaving(false); }
  };
  const closeTemplatePreview = () => {
    if (templateMutationInFlight.current) return;
    if (!activationOpen) setSelectedTemplate(undefined);
    setActivationOpen(false);
    setActivationError('');
  };

  if (templateEditorOpen && canMutate) {
    return <ChecklistTemplateEditor
      value={templateDraft}
      editing={editingTemplate}
      onChange={(next) => {
        setTemplateDraft(next);
        setTemplateIdempotencyKey(crypto.randomUUID());
      }}
      onClose={() => { if (!templateMutationInFlight.current) { setTemplateEditorOpen(false); setSelectedTemplate(editingTemplate); setEditingTemplate(undefined); setTemplateError(''); } }}
      onSubmit={() => void saveTemplate()}
      saving={templateSaving}
      error={templateError}
    />;
  }

  const checklistColumns: GridColDef<ChecklistRow>[] = [
    { field: 'worker', headerName: '대상 구성원', flex: 1, minWidth: 180 },
    { field: 'type', headerName: '구분', width: 110, renderCell: ({ value }) => <ValueBadge label={value} /> },
    { field: 'targetOn', headerName: '기준일', width: 120 },
    { field: 'progress', headerName: '진행률', width: 90 },
    { field: 'status', headerName: '상태', flex: 1, minWidth: 140, renderCell: ({ row }) => <StatusBadge {...checklistStatus(row.source.status)} /> },
  ];
  const templateColumns: GridColDef<ChecklistTemplate>[] = [
    { field: 'descriptor', headerName: '템플릿 이름', flex: 1, minWidth: 220 },
    { field: 'lifecycleType', headerName: '구분', width: 110, valueFormatter: (value) => lifecycleLabel(String(value)), renderCell: ({ value }) => <ValueBadge label={lifecycleLabel(String(value))} /> },
    { field: 'workerScope', headerName: '대상', width: 120, valueGetter: (_value, row) => workerScopeLabel(row), renderCell: ({ value }) => <ValueBadge label={value} /> },
    { field: 'itemCount', headerName: '항목', width: 80, type: 'number', valueGetter: (_value, row) => row.items.length, valueFormatter: (value) => `${value}개` },
    { field: 'version', headerName: '버전', width: 80, type: 'number', valueFormatter: (value) => `v${value}` },
    { field: 'active', headerName: '상태', flex: 1, minWidth: 120, renderCell: ({ row }) => <StatusBadge label={row.active ? '사용 중' : '사용 종료'} tone={row.active ? 'success' : 'neutral'} /> },
  ];
  const checklistState: ResourceCollectionState = resource.loading
    ? { kind: 'loading' }
    : resource.error
      ? { kind: 'error', message: resource.error.message, onRetry: resource.reload }
      : visibleRows.length === 0
        ? { kind: 'empty', title: query ? '검색 조건에 맞는 체크리스트가 없습니다.' : '표시할 체크리스트가 없습니다.' }
        : { kind: 'ready' };
  const templateState: ResourceCollectionState = templatesResource.loading
    ? { kind: 'loading' }
    : templatesResource.error
      ? { kind: 'error', message: templatesResource.error.message, onRetry: templatesResource.reload }
      : visibleTemplates.length === 0
        ? { kind: 'empty', title: templateQuery ? '검색 조건에 맞는 템플릿이 없습니다.' : '등록된 체크리스트 템플릿이 없습니다.' }
        : { kind: 'ready' };
  const pageAction = canMutate
    ? activeView === 'checklists'
      ? <Button variant="fill" startIcon={<AddOutlined />} onClick={() => { setOnboardingOnly(false); setWorkerID(''); setTemplateID(''); setWorkerQuery(''); setTemplatePickerQuery(''); setTargetOn(''); setError(undefined); setDialogOpen(true); }}>체크리스트 시작</Button>
      : <Button variant="fill" startIcon={<AddOutlined />} onClick={() => openTemplateEditor()}>새 템플릿</Button>
    : undefined;
  const checklistDetail = selected && <DetailPanel
    open={detailOpen}
    onClose={() => setDetailOpen(false)}
    title={selected.descriptor}
    closeLabel="체크리스트 목록으로 돌아가기"
    meta={<>{workersResource.data?.find((worker) => worker.id === selected.workerId)?.name ?? '구성원'} · <ValueBadge label={lifecycleLabel(selected.lifecycleType)} /> · {selected.targetOn}</>}
    status={<StatusBadge {...checklistStatus(selected.status)} />}
  >
    <DetailDrawerBody padding="compact">
      {completionError?.checklistID === selected.id && <Alert severity="error">{completionError.message}</Alert>}
      <Stack divider={<Divider />}>
        {selected.items.map((item) => <Stack key={item.id} spacing={1.5} sx={{ py: 1.5 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>{item.descriptor}</Typography>
            <StatusBadge tone={item.status === 'COMPLETED' ? 'success' : item.status === 'FAILED' ? 'danger' : 'neutral'} label={itemStatusLabel(item.status)} />
          </Stack>
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}><ValueBadge label={executionLabel(item.executionType)} />{item.required && <ValueBadge label="필수" />}</Stack>
          <Typography variant="caption" color="text.secondary">기한 {item.dueAt ? formatTimestampDate(item.dueAt) : '미지정'}{item.completedAt ? ` · 완료 ${formatTimestampDate(item.completedAt)}` : ''}</Typography>
          {item.note && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.note}</Typography>}
          {canMutate && item.executionType === 'MANUAL' && item.status !== 'COMPLETED' && <Button size="small" variant="outlined" sx={{ alignSelf: 'flex-start' }} disabled={Boolean(completingID)} onClick={() => complete(item.id)}>완료</Button>}
        </Stack>)}
      </Stack>
    </DetailDrawerBody>
  </DetailPanel>;

  return <>
    <ResourcePage
      title="온·오프보딩"
      primaryAction={pageAction}
      tabs={<PillTabs value={activeView} items={lifecycleTabs} ariaLabel="온·오프보딩 화면" onChange={changeView} />}
    >
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {error && !dialogOpen && <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }}>{error}</Alert>}
        <FeedbackSnackbar open={Boolean(templateNotice) && activeView === 'templates'} message={templateNotice} tone="success" onClose={() => setTemplateNotice('')} />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {activeView === 'checklists' ? <Box role="tabpanel" id="lifecycle-checklists-panel" aria-labelledby="lifecycle-checklists-tab" sx={{ height: '100%', minHeight: 0 }}>
            <ListDetailWorkspace open={Boolean(selected) && detailOpen} detail={checklistDetail} selectionKey={selected?.id}>
            <ResourceCollection
              state={checklistState}
              rows={visibleRows}
              totalCount={rows.length}
              columns={checklistColumns}
              query={query}
              onQueryChange={setQuery}
              searchLabel="체크리스트 검색"
              searchPlaceholder="구성원, 구분 또는 상태 검색"
              ariaLabel="온·오프보딩 체크리스트 목록"
              filters={[]}
              onRowClick={({ row }) => { setSelected(row.source); setDetailOpen(true); }}
            />
            </ListDetailWorkspace>
          </Box> : <Box role="tabpanel" id="lifecycle-templates-panel" aria-labelledby="lifecycle-templates-tab" sx={{ height: '100%', minHeight: 0 }}>
            <ResourceCollection
              state={templateState}
              rows={visibleTemplates}
              totalCount={templates.length}
              columns={templateColumns}
              query={templateQuery}
              onQueryChange={setTemplateQuery}
              searchLabel="템플릿 검색"
              searchPlaceholder="템플릿 이름 검색"
              ariaLabel="체크리스트 템플릿 목록"
              filters={[]}
              onRowClick={({ row }) => setSelectedTemplate(row)}
            />
          </Box>}
        </Box>
      </Box>
    </ResourcePage>

    <FormDialog
      open={canMutate && dialogOpen}
      onClose={() => { if (submitLoading) return; setDialogOpen(false); route.replace('checklists'); }}
      onSubmit={create}
      title={onboardingOnly ? '온보딩 시작' : '체크리스트 시작'}
      cancelLabel="취소"
      submitLabel="시작"
      busy={submitLoading}
      busyLabel="생성 중"
      submitDisabled={!selectedWorker || !templateID || !targetOn || Boolean(workersResource.error) || Boolean(templatesResource.error)}
    >
      {error && <Alert severity="error">{error}</Alert>}
      {workerID && !workersResource.loading && !workersResource.error && !selectedWorker && <Alert severity="warning">연결된 구성원을 찾을 수 없습니다. 현재 조회할 수 있는 구성원을 다시 선택해 주세요.</Alert>}
      {(workersResource.error || templatesResource.error) && <Alert
        severity="error"
        action={<Button color="inherit" size="small" onClick={() => { workersResource.reload(); templatesResource.reload(); }}>다시 시도</Button>}
      >대상 구성원 또는 템플릿을 불러오지 못했습니다.</Alert>}
      <FormAutocomplete options={workersResource.data ?? []}
        value={selectedWorker ?? null} inputValue={selectedWorker ? [selectedWorker.name, selectedWorker.detail].filter(Boolean).join(' · ') : workerQuery}
        getOptionLabel={(worker) => [worker.name, worker.detail].filter(Boolean).join(' · ')}
        getOptionKey={(option) => option.id} isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={submitLoading}
        request={workersResource.error ? { status: 'error', message: '구성원을 불러오지 못했습니다.', onRetry: workersResource.reload } : { status: workersResource.loading ? 'loading' : 'ready' }}
        onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setWorkerQuery(value); setWorkerID(''); setTemplateID(''); setTemplatePickerQuery(''); } }}
        onChange={(_, worker) => { setWorkerQuery(''); setWorkerID(worker?.id ?? ''); setTemplateID(''); setTemplatePickerQuery(''); }}
        input={{ label: '대상 구성원', required: true, placeholder: '이름·직무·소속으로 검색' }} />
      <FormAutocomplete options={eligibleActiveTemplates}
        value={eligibleActiveTemplates.find((template) => template.id === templateID) ?? null}
        inputValue={eligibleActiveTemplates.find((template) => template.id === templateID)?.descriptor ?? templatePickerQuery}
        getOptionLabel={(template) => template.descriptor}
        getOptionKey={(option) => option.id} isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={({ key, ...props }, template) => <Box component="li" key={key} {...props}><Stack><Typography variant="body2">{template.descriptor}</Typography><Typography variant="caption" color="text.secondary">{lifecycleLabel(template.lifecycleType)} · {workerScopeLabel(template)} · v{template.version}</Typography></Stack></Box>}
        disabled={submitLoading || !workerID}
        request={templatesResource.error ? { status: 'error', message: '템플릿을 불러오지 못했습니다.', onRetry: templatesResource.reload } : { status: templatesResource.loading ? 'loading' : 'ready' }}
        noOptionsText="사용할 수 있는 템플릿이 없습니다."
        onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setTemplatePickerQuery(value); setTemplateID(''); } }}
        onChange={(_, template) => { setTemplatePickerQuery(''); setTemplateID(template?.id ?? ''); }}
        input={{ label: '체크리스트 템플릿', required: true, placeholder: '템플릿 이름으로 검색', helperText: !workerID ? '대상 구성원을 먼저 선택해 주세요.' : selectedWorkerType ? '선택한 구성원 유형에 맞는 사용 중 템플릿을 표시합니다.' : '구성원 유형을 확인할 수 없어 전체 구성원 템플릿만 표시합니다.' }} />
      <FormTextField type="date" label="기준일" value={targetOn} onChange={(event) => setTargetOn(event.target.value)} />
    </FormDialog>

    <DialogSurface
      open={Boolean(selectedTemplate)}
      onClose={closeTemplatePreview}
      title={activationOpen ? selectedTemplate?.active ? '템플릿 사용 종료' : '템플릿 다시 사용' : selectedTemplate?.descriptor ?? ''}
      description={activationOpen ? selectedTemplate?.descriptor : undefined}
      titleAdornment={!activationOpen && selectedTemplate ? <StatusBadge label={selectedTemplate.active ? '사용 중' : '사용 종료'} tone={selectedTemplate.active ? 'success' : 'neutral'} /> : undefined}
      closeLabel={activationOpen ? '템플릿 상세로 돌아가기' : '상세 닫기'}
      size="standard"
      busy={activationSaving}
      onSubmit={activationOpen && canMutate ? changeActivation : undefined}
      actions={canMutate && selectedTemplate ? activationOpen ? <>
        <Button autoFocus variant="weak" disabled={activationSaving} onClick={closeTemplatePreview}>돌아가기</Button>
        <Button type="submit" variant="fill" color={selectedTemplate.active ? 'error' : 'primary'} disabled={activationSaving}>{activationSaving ? '처리 중' : selectedTemplate.active ? '사용 종료' : '다시 사용'}</Button>
      </> : <>
        <Button ref={activationButton} variant="weak" color={selectedTemplate.active ? 'error' : 'primary'} onClick={() => { setActivationError(''); setActivationOpen(true); }}>{selectedTemplate.active ? '템플릿 사용 종료' : '템플릿 다시 사용'}</Button>
        {selectedTemplate.active && <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => openTemplateEditor(selectedTemplate)}>새 버전 만들기</Button>}
      </> : undefined}
    >
      {activationOpen && selectedTemplate ? <Stack spacing={2}>
        <Alert severity={selectedTemplate.active ? 'warning' : 'info'}>
          {selectedTemplate.active
            ? '신규 체크리스트에서 선택할 수 없게 됩니다. 이미 시작한 체크리스트와 이력은 그대로 유지됩니다.'
            : '가장 최신 버전을 신규 체크리스트에서 다시 선택할 수 있게 합니다.'}
        </Alert>
        {activationError && <Alert severity="error">{activationError}</Alert>}
      </Stack> : selectedTemplate && <Stack spacing={3}>
        <DefinitionList items={[
          ['구분', <ValueBadge label={lifecycleLabel(selectedTemplate.lifecycleType)} />],
          ['대상', <ValueBadge label={workerScopeLabel(selectedTemplate)} />],
          ['버전', `v${selectedTemplate.version}`],
          ['항목', `${selectedTemplate.items.length}개`],
        ]} />
        <Box component="section" aria-label="체크리스트 항목">
          <Typography component="h3" variant="subtitle1" sx={{ mb: 1 }}>체크리스트 항목</Typography>
          <Stack divider={<Divider />}>
            {selectedTemplate.items.map((item, index) => <Box key={item.id} sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                <Chip size="small" label={index + 1} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1, minWidth: 0, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{item.descriptor}</Typography>
                    <Typography variant="caption" color="text.secondary">기준일 {item.dueOffsetDays === 0 ? '당일' : item.dueOffsetDays > 0 ? `${item.dueOffsetDays}일 후` : `${Math.abs(item.dueOffsetDays)}일 전`}{item.required ? ' · 필수' : ''}</Typography>
                  </Box>
                  <ValueBadge label={item.executionType === 'INTEGRATION' ? integrationLabel(item.integrationTarget) : '담당자 처리'} />
                </Stack>
              </Stack>
            </Box>)}
          </Stack>
        </Box>
      </Stack>}
    </DialogSurface>
  </>;
}
